<template>
  <b-modal
    :id="modalId"
    :busy="submitting"
    title="Create a requirement"
    ref="createreqmodal"
    name="create-requirement-modal"
    size="lg"
    ok-title="Create"
    close-title="Cancel"
    hide-header-close
    :ok-disabled="!name || !selectedRequirementType || loading"
    @shown="shown"
    @ok="handleOk"
    @cancel="handleCancel">
    <form id="create-requirement-form">
      <b-form-group :feedback="nameInvalidFeedback"
                    :state="nameState">
        <b-form-input v-model="name"
                      type="text"
                      placeholder="Requirement name"
                      data-vv-name="name"
                      v-validate="'required'">
        </b-form-input>
        <!-- THIS errors.has('name') was causing the whole element to be #comment -->
        <!--<icon v-show="errors.has('name')" name="warning"></icon>-->
        <!--<span v-show="errors.has('name')" class="help is-danger">{{ errors.first('name') }}</span>-->
      </b-form-group>
      <b-form-group description="Set a custom ID for this Requirement">
        <b-form-input v-model="displayID"
                      type="text"
                      placeholder="Requirement ID (optional)">
        </b-form-input>
      </b-form-group>
      <b-form-group description="These types can be managed on the rmCloud Configuration screen"
                    :feedback="requirementTypeInvalidFeedback"
                    :state="requirementTypeState">
        <multiselect v-model="selectedRequirementType"
                     :options="requirementTypes"
                     :loading="requirementTypesLoading"
                     :allow-empty="false"
                     :close-on-select="true"
                     :hide-selected="false"
                     placeholder="Select a requirement type"
                     label="name"
                     track-by="requirement_type_id">
        </multiselect>
      </b-form-group>
      <b-form-group>
        <b-form-textarea v-model="description"
                         placeholder="Description"
                         :rows="2"
                         :max-rows="6">
        </b-form-textarea>
      </b-form-group>
      <b-form-group v-if="!currentProjectIsGlobal">
        <multiselect v-model="selectedStatus"
                     :options="statuses"
                     :loading="statusesLoading"
                     :close-on-select="true"
                     :hide-selected="false"
                     placeholder="Status"
                     label="name"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-form-group>
        <multiselect v-model="selectedRequirements"
                     :options="availableRequirements"
                     :multiple="true"
                     :loading="requirementsLoading"
                     :custom-label="customRequirementLabel"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     :placeholder="getRequirementsPlaceholder()"
                     track-by="requirement_id">
        </multiselect>
      </b-form-group>
      <b-form-group id="issues-multiselect-group-create">
        <multiselect v-model="selectedIssues"
                     :options="sortedIssues"
                     :multiple="true"
                     :loading="issuesLoading"
                     :custom-label="customIssueLabel"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     :disabled="shouldDisableIssuesMultiselect"
                     placeholder="Select issue(s)"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-tooltip v-if="shouldDisableIssuesMultiselect" target="issues-multiselect-group-create" triggers="hover">
        No issues found on this Jira project!
      </b-tooltip>
      <b-form-group v-if="$auth.user().installed_on_confluence">
        <multiselect v-model="selectedContents"
                     :options="contents"
                     :loading="contentsLoading"
                     :multiple="true"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     :max="20"
                     placeholder="Select Confluence content"
                     label="title"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-form-group v-if="!currentProjectIsGlobal">
        <multiselect v-model="selectedComponents"
                     :options="components"
                     :multiple="true"
                     :loading="componentsLoading"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     placeholder="Component(s)"
                     label="name"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-form-group v-if="!currentProjectIsGlobal">
        <multiselect v-model="selectedAssignee"
                     :options="assignees"
                     :loading="assigneesLoading"
                     :close-on-select="true"
                     :hide-selected="false"
                     placeholder="Assign to"
                     label="displayName"
                     track-by="key">
        </multiselect>
      </b-form-group>
      <b-form-group v-for="attribute in attributes" :key="attribute.attribute_id">
        <div v-if="attribute.type === 'select'">
          <label>{{attribute.name}}</label>
          <b-form-select v-model="selectedAttributes[attribute.attribute_id]"
                         :options="getOptionsArray(attribute.options)">

          </b-form-select>
        </div>
        <b-form-input v-else
                      type="text"
                      :placeholder="attribute.name"
                      v-model="selectedAttributes[attribute.attribute_id]">
        </b-form-input>
      </b-form-group>
      <span v-if="currentProject">This will be added to project <strong>{{ currentProject.name }}</strong></span>
    </form>
    <div slot="modal-ok" v-if="submitting">
      <icon name="spinner" pulse></icon> Creating
    </div>
  </b-modal>
</template>

<script>
  import dialogMixin from '@/mixins/dialog'
  import requirementModalMixin from '@/mixins/requirementModal'
  import utilsMixin from '@/mixins/utils'
  export default {
    computed: {
      currentProject () {
        return this.$store.state.currentProject
      }
    },
    watch: {
      selectedRequirementType (currentValue) {
        // Remove selected requirements if the type changes
        this.selectedRequirements = []
      }
    },
    data () {
      return {}
    },
    props: {
      modalId: String,
      parentFolderId: {
        default: null,
        type: Number
      }
    },
    mixins: [dialogMixin, requirementModalMixin, utilsMixin],
    methods: {
      handleOk (e) {
        e.preventDefault()
        // This validateAll is needed for the case when nothing has been entered yet
        // Otherwise it was letting it validate
        // The empty then/catch avoids a console error
        this.$validator.validateAll().then(() => {
        }).catch(() => {
        })
        if (this.errors.any()) {
          return e.cancel()
        } else {
          this.submitting = true
          this.submitData()
        }
      },
      handleCancel (e) {
        this.clearModal()
      },
      shown () {
        this.getAttributes()
        this.getIssues()
        this.getRequirements()
        this.getRequirements()
        this.getRequirementTypes()
        if (this.$auth.user().installed_on_confluence) {
          this.getContents()
        }
        if (!this.currentProjectIsGlobal) {
          this.getComponents()
          this.getStatusesForCurrentProject()
          this.getAssignees()
        }
      },
      clearModal () {
        this.name = ''
        this.displayID = null
        this.description = ''
        this.submitting = false
        this.issuesLoading = false
        this.contentsLoading = false
        this.statusesLoading = false
        this.assigneesLoading = false
        this.requirementsLoading = false
        this.componentsLoading = false
        this.selectedAssignee = null
        this.selectedAttributes = {}
        this.selectedComponents = []
        this.selectedContents = []
        this.selectedIssues = []
        this.selectedRequirements = []
        this.selectedRequirementType = null
        this.selectedStatus = null
        this.$nextTick(() => {
          this.errors.clear()
        })
      },
      submitData () {
        this.$ga.event('requirement', 'create', this.name, 1)
        this.$http.post(
          '/api/requirement',
          {
            assigned_to: this.selectedAssignee,
            attributes: this.selectedAttributes,
            components: this.selectedComponents,
            contents: this.selectedContents,
            description: this.description,
            display_id: this.displayID,
            issues: this.selectedIssues,
            name: this.name,
            project: this.currentProject,
            parent_folder_id: this.parentFolderId,
            requirements: this.selectedRequirements,
            requirement_type_id: this.selectedRequirementType.requirement_type_id,
            status: this.selectedStatus
          }
        ).then(response => {
          window.AP.flag.create({
            title: 'Success!',
            body: 'Requirement created successfully',
            type: 'success',
            close: 'auto'
          })
          this.clearModal()
          this.setTheState()
          this.$refs.createreqmodal.hide()
        }, error => {
          let errorString = this.createErrorStringFromResponse(error.response.data)
          window.AP.flag.create({
            title: 'Error',
            body: `There was a problem saving your requirement: ${errorString}`,
            type: 'error'
          })
          this.submitting = false
        })
      },
      setTheState () {
        this.$store.commit('setNeedToRefreshItemsList', true)
      }
    }
  }
</script>

<style>
  .multiselect__tag {
    max-width: 100%;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
  .required-text {
    color: red;
  }
</style>



// WEBPACK FOOTER //
// CreateRequirementModal.vue?6dd08f2b